import React, { useState } from 'react';
import { connect } from 'react-redux';
import { showDate, showHour } from '../../utils/DataFormatterUtils';
import { ImageThumbnails } from '../image_thumbnails/ImageThumbnails';
import { getFileMimeTypeFromString } from '../../utils/FileAccessUtils';
import { getObjectValues, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { Button, Popover, Spin } from 'antd';
import { EyeOutlined, MailOutlined } from '@ant-design/icons';
import {
	contactsRestCrudThunksForBuyer,
	contactsRestCrudThunksForSupplier,
} from '../../thunks/contacts_thunks';
import moment from 'moment';
import './index.less';
import { UserName } from '../name_component/user/UserNameComponent';
import { EmailUserName } from '../email_user_name/EmailUserNameComponent';
import EmailOriginModal from './EmailOriginModal';
import AudioPlayer from './AudioPlayer';
import AIIcon from '../AIIcon';
import NoteImageGrid from '../note_image_grid/NoteImageGrid';

const MessageBubble = ({
	currentUser,
	n,
	idx,
	subscribers = [],
	all_contacts,
	getContacts,
	userType,
	isFromInternalNotes = false,
	id,
	isHighlighted = false,
	extraContent = null,
}) => {
	const [seenByOpen, setSeenByOpen] = useState(false);
	const [seenBydata, setSeenBydata] = useState([]);
	const [originVisible, setOriginVisible] = useState(false);
	const [showTranscription, setShowTranscription] = useState(false);

	const notesReadyBy =
		n.noteReadDetails &&
		n.noteReadDetails.filter((contact) => contact.readBy !== currentUser.email);
	function findUsersByEmail(users, readByArray) {
		const userEmails = readByArray.map((entry) => entry.readBy);
		const uniqueUserEmails = Array.from(new Set(userEmails));

		const usersWithReadByEmail = users.filter((user) => uniqueUserEmails.includes(user.email));
		const emailsNotInUserArray = uniqueUserEmails.filter(
			(email) => !users.find((user) => user.email === email)
		);

		return { usersWithReadByEmail, emailsNotInUserArray };
	}

	const handleClickChange = (open) => {
		setSeenByOpen(open);

		if (open) {
			const { usersWithReadByEmail, emailsNotInUserArray } = findUsersByEmail(
				(subscribers || []).concat(
					getObjectValues(all_contacts.records).map((el) => ({
						...el,
						key: el.email,
					}))
				),
				notesReadyBy
			);
			if (emailsNotInUserArray.length) {
				getContacts(emailsNotInUserArray).then((data = []) => {
					const { usersWithReadByEmail } = findUsersByEmail(
						(subscribers || [])
							.concat(
								getObjectValues(all_contacts.records).map((el) => ({
									...el,
									key: el.email,
								}))
							)
							.concat(
								getObjectValues(data).map((el) => ({
									...el,
									key: el.email,
								}))
							),
						notesReadyBy
					);
					setSeenBydata(usersWithReadByEmail);
				});
			} else {
				setSeenBydata(usersWithReadByEmail);
			}
		}
	};

	const getName = (userContact) => {
		return `-- ${nullSafeGetOrElse(
			'noteAddedByContact.nameGiven',
			userContact,
			nullSafeGetOrElse('noteAddedBy', userContact, '')
		)} ${nullSafeGetOrElse('noteAddedByContact.nameFamily', userContact, '')}`;
	};
	const me = n.noteAddedBy === currentUser.email;

	// Seen-by read receipt (count + eye) only shows on notes I sent that others have read.
	// It renders inline next to the timestamp so time and eye share one line.
	const showReadReceipt = me && notesReadyBy && nullSafeGetOrElse('length', notesReadyBy, 0) > 0;

	// A note's image(s) render as a collage grid inside the normal message bubble —
	// same bubble, name, timestamp and read receipt as a text note; the grid simply
	// takes the place of the text (a note may carry both text and photos).
	const photosToShow = n.photos && n.photos.length > 0 ? n.photos : n.photo ? [n.photo] : [];

	const constructMessage = (message) => {
		if (!message.elements) return message.text;
		return message.elements.map((n) => {
			if (
				n.type === 'user' &&
				(nullSafeGet('userObject.nameGiven', n) || nullSafeGet('user.nameGiven', n))
			) {
				return (
					<UserName
						contact={n.userObject || n.user}
						renderName={(contact) => (
							<span className={`${me ? `bg-[#0069cc]` : `bg-[#c7cfd6]`} cursor-pointer`}>
								{nullSafeGetOrElse('userObject.nameGiven', n, nullSafeGet('user.nameGiven', n))}{' '}
								{nullSafeGetOrElse('userObject.nameFamily', n, nullSafeGet('user.nameFamily', n))}
							</span>
						)}
					/>
				);
			} else return <span>{n.value}</span>;
		});
	};

	const _hideOriginModal = () => setOriginVisible(false);
	const _showOriginModal = () => setOriginVisible(true);

	const marginClass = me
		? isFromInternalNotes
			? 'mr-[80px]'
			: 'mr-[120px]'
		: isFromInternalNotes
		? 'ml-[80px]'
		: 'ml-[120px]';

	return (
		<div
			id={id}
			className={`chatItem ${me ? 'me' : 'him'} ${marginClass}${
				isHighlighted ? ' chatItemFlash' : ''
			}`}
			key={idx}
		>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				{extraContent ? <div className="mb-2">{extraContent}</div> : null}
				{n.audio ? (
					<>
						<AudioPlayer src={n.audio} isOwnMessage={me} />
						{(n.text || (n.elements && n.elements.length > 0)) && (
							<>
								{showTranscription && (
									<div
										style={{
											fontSize: 16,
											whiteSpace: 'pre-wrap',
											wordBreak: 'break-word',
											marginTop: 8,
										}}
									>
										{constructMessage(n)}
									</div>
								)}
								<div style={{ marginTop: 8 }}>
									<Button
										type="link"
										onClick={() => setShowTranscription(!showTranscription)}
										className="transcription-button"
									>
										{showTranscription ? 'Hide transcription' : 'Show transcription'}
									</Button>
								</div>
							</>
						)}

						{n.isAudioTranslated ? (
							<div className="FootnoteRegular extraTextBottom flex items-center gap-1 text-character-secondary">
								<AIIcon className={` ${me ? 'fill-white' : 'fill-character-secondary'}`} />{' '}
								<span> Audio translated.</span>
							</div>
						) : null}

						{n.isAudioTranscriptionError ? (
							<div className="FootnoteRegular extraTextBottom text-character-secondary">
								Transcription unavailable.
							</div>
						) : null}
					</>
				) : n.text || (n.elements && n.elements.length > 0) ? (
					<div style={{ fontSize: 16, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
						{constructMessage(n)}
					</div>
				) : null}
				{photosToShow.length > 0 ? <NoteImageGrid photos={photosToShow} /> : null}
				<div
					style={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						alignItems: 'flex-end',
						marginTop: 4,
					}}
				>
					<div
						style={{
							display: 'flex',
							fontSize: '12px',
							color: '#aaa',
						}}
						className="mr-2"
					>
						{n.noteAddedBy !== currentUser.email && getName(n)}
						{n.s3EmailReferenceUrl ? (
							<Popover trigger="hover" content="Show Original">
								<div className="ml-2 cursor-pointer" onClick={_showOriginModal}>
									<MailOutlined />
								</div>
							</Popover>
						) : null}
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
						<div
							style={{
								display: 'flex',
								fontSize: '10px',
								color: me ? 'rgba(209,230,250,1)' : '#aaa',
							}}
						>
							{showHour(n.noteAddedAt, 'MMM DD')}
						</div>
						{showReadReceipt ? (
							<div className="flex items-center" style={{ fontSize: 12, lineHeight: 1 }}>
								<span>{nullSafeGet('length', notesReadyBy)}</span>
								<Popover
									placement="left"
									title="Seen by"
									trigger="click"
									onVisibleChange={(open) => handleClickChange(open)}
									visible={seenByOpen}
									content={
										<div className="no-scrollbar flex max-h-60 cursor-pointer  flex-col overflow-y-scroll pr-2 ">
											{(notesReadyBy &&
												seenBydata.length &&
												notesReadyBy.map(({ readBy, readAt }) => {
													const contact = seenBydata.find((el) => el.email === readBy);
													if (nullSafeGet('nameGiven', contact))
														return (
															<div className="mb-3 flex flex-row items-center">
																<UserName
																	mode={'avatar'}
																	contact={contact}
																	placement={'bottomRight'}
																/>
																<div className="inline-block w-full pl-3">
																	<div className="flex w-full items-center justify-between">
																		{contact.nameGiven} {contact.nameFamily}{' '}
																		<div className="ml-2 text-xs text-slate-400">
																			{moment(readAt).fromNow()}
																		</div>
																	</div>
																	<div>{contact.email}</div>
																</div>
															</div>
														);
													else return null;
												})) || <Spin />}
										</div>
									}
								>
									<EyeOutlined className="ml-1" style={{ fontSize: 13, display: 'block' }} />
								</Popover>
							</div>
						) : null}
					</div>
				</div>
			</div>
			{n.video ? (
				<video width="100%" height="auto" controls>
					<source src={n.video} type={getFileMimeTypeFromString(n.video)} />
					Your browser does not support the video tag.
				</video>
			) : null}

			<div className="time">
				{n.noteAddedBy === currentUser.email
					? [
							<div key={1} style={{ marginRight: 8 }}>
								{n.noteAddedByContact ? (
									<UserName mode={'avatar'} contact={n.noteAddedByContact} placement={'left'} />
								) : (
									<EmailUserName email={n.noteAddedBy} />
								)}
							</div>,
							<div key={2}>{showDate(n.noteAddedAt, 'MMM DD')}</div>,
					  ]
					: [
							<div key={1} style={{ marginLeft: 8 }}>
								{n.noteAddedByContact ? (
									<UserName mode={'avatar'} contact={n.noteAddedByContact} placement={'right'} />
								) : (
									<EmailUserName email={n.noteAddedBy} placement={'right'} />
								)}
							</div>,
							<div key={2}>{showDate(n.noteAddedAt, 'MMM DD')}</div>,
					  ]}
			</div>
			{originVisible ? (
				<EmailOriginModal s3Url={n.s3EmailReferenceUrl} onCancel={_hideOriginModal} />
			) : null}
		</div>
	);
};

const mapStateToProps = (state) => ({
	all_contacts: state.all_contacts,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getContacts: (ids) => {
		if (ownProps.userType === 'supplier') {
			return dispatch(
				contactsRestCrudThunksForSupplier.readMultiple(ids, 'contactsIndex', null, null, null, true)
			);
		}
		return dispatch(
			contactsRestCrudThunksForBuyer.readMultiple(ids, 'contactsIndex', null, null, null, true)
		);
	},
});

export default connect(mapStateToProps, mapDispatchToProps)(MessageBubble);
